import React, { useState} from "react";
import { Link } from "react-router-dom";
import { FaCalculator, FaClipboardList, FaFileInvoiceDollar, FaUsers, FaShieldAlt, FaChartLine } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const LeftSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  max-height: 400px;
  animation: ${slideInLeft} 1s ease-in-out;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ExperienceBadge = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 10px 18px;
  font-size: 18px;
  font-weight: bold;
`;

const ServiceBox = styled.div`
  background: #f7f9fa;
  border-left: 4px solid #228B22;
  padding: 20px;
  margin: 10px 0;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
    transform: translateY(-5px);
  }
`;

const PayrollManagement = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    { 
      question: "Pourquoi externaliser la paie de mon entreprise ?", 
      answer: "L'externalisation de la paie vous permet de gagner du temps, de réduire les risques d'erreurs, de rester conforme à la législation en constante évolution et de bénéficier de l'expertise de professionnels." 
    },
    { 
      question: "Quels sont les risques d'une mauvaise gestion de la paie ?", 
      answer: "Les risques incluent des erreurs dans le calcul des salaires, des déclarations sociales incorrectes, des litiges avec les employés, des pénalités financières et des contrôles de la CNSS." 
    },
    { 
      question: "Quels types d'entreprises peuvent bénéficier de vos services de paie ?", 
      answer: "Nous nous adressons à toutes les entreprises, quelle que soit leur taille ou leur secteur d'activité, qui souhaitent externaliser leur gestion de la paie." 
    },
    { 
      question: "Quels services de paie propose CINEF-EC ?", 
      answer: "Nous proposons un service complet incluant l'établissement des bulletins de paie, les déclarations sociales, la gestion des absences et des congés, le suivi des entrées et sorties du personnel, et le conseil en droit social." 
    },
    {
      question: "Comment garantissez-vous la confidentialité des données de nos salariés ?",
      answer: "Nous sommes soumis au secret professionnel et nous mettons en place des mesures de sécurité rigoureuses pour protéger vos données."
    },
    {
      question: "Proposez vous des services de gestion des ressources humaines ?",
      answer: "Oui, nous pouvons vous accompagner dans la mise en place de processus RH, la gestion de vos recrutements, la formation de vos salariés, etc."
    }
  ];

  const services = [
    {
      title: "Établissement des bulletins de paie",
      description: "Calcul des salaires, gestion des absences et des congés, établissement des déclarations sociales."
    },
    {
      title: "Gestion des contrats de travail",
      description: "Rédaction des contrats, suivi des obligations légales, gestion des procédures de rupture de contrat."
    },
    {
      title: "Conseil en droit social",
      description: "Assistance en cas de litiges, information sur les obligations légales, accompagnement lors des contrôles sociaux."
    },
    {
      title: "Gestion des déclarations sociales",
      description: "Préparation et envoi des déclarations obligatoires aux organismes sociaux dans les délais requis."
    },
    {
      title: "Aide aux recrutements",
      description: "Définition de profils de poste, présélection de candidats, assistance aux entretiens d'embauche."
    },
    {
      title: "Gestion des formations",
      description: "Élaboration du plan de formation, suivi des obligations légales, recherche de financements."
    }
  ];

  return (
    <>
      <Header />

      <section className="service-banner d-flex align-items-center">
        <div className="container text-white text-start">
          <h1 className="fw-bold">Paie et Gestion des Salariés</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white text-decoration-none">
                  Accueil
                </Link>{" "}
                /{" "}
                <Link to="/services" className="text-white text-decoration-none">
                  Services
                </Link>
              </li>
              <li className="breadcrumb-item active text-white" aria-current="page">
                Paie et Gestion des Salariés
              </li>
            </ol>
          </nav>
        </div>
      </section>

      <section className="service-content py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <h2 className="fw-bold">La Gestion de la Paie et des Ressources Humaines</h2>
              <p className="text-muted">
                La gestion de la paie et des ressources humaines est un enjeu complexe et chronophage pour les entreprises. 
                CINEF-EC vous accompagne dans la gestion de vos obligations sociales, pour vous permettre de vous concentrer 
                sur votre activité principale.
              </p>
              
              <h4 className="fw-bold mt-4">Pourquoi nous confier la gestion de votre paie et de vos salariés ?</h4>
              <ul className="text-muted">
                <li><strong>Expertise pointue :</strong> Nos spécialistes maîtrisent les subtilités de la législation sociale et conventionnelle, pour garantir la conformité de votre gestion de la paie.</li>
                <li><strong>Gain de temps et d'efficacité :</strong> Nous prenons en charge toutes les tâches administratives liées à la paie et à la gestion des salariés, vous permettant de vous concentrer sur votre cœur de métier.</li>
                <li><strong>Sécurité et confidentialité :</strong> Nous assurons la fiabilité et la confidentialité du traitement de vos données sociales.</li>
                <li><strong>Veille sociale permanente :</strong> Nous vous informons des évolutions légales et conventionnelles, pour vous garantir une gestion sociale toujours à jour.</li>
                <li><strong>Accompagnement personnalisé :</strong> Nous nous adaptons à vos besoins spécifiques, que vous soyez une TPE, une PME ou un grand groupe.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="advantages py-5 text-center">
        <div className="container">
          <h3 className="fw-bold" >Nos Services Clés</h3>
          <div className="row mt-4">
            <div className="col-md-3">
              <FaCalculator className="advantage-icon text-primary" size={50} />
              <p className="fw-bold">Calcul de Paie</p>
            </div>
            <div className="col-md-3">
              <FaClipboardList className="advantage-icon text-success" size={50} />
              <p className="fw-bold">Gestion Administrative</p>
            </div>
            <div className="col-md-3">
              <FaFileInvoiceDollar className="advantage-icon text-danger" size={50} />
              <p className="fw-bold">Déclarations Sociales</p>
            </div>
            <div className="col-md-3">
              <FaUsers className="advantage-icon text-warning" size={50} />
              <p className="fw-bold">Suivi des Effectifs</p>
            </div>
          </div>
        </div>
      </section>

      <section className="services-detail py-5 bg-light">
        <div className="container">
          <h3 className="text-center fw-bold mb-4">Notre Offre Complète</h3>
          <div className="row">
            {services.map((service, index) => (
              <div className="col-md-6" key={index}>
                <ServiceBox>
                  <h5 className="fw-bold">{service.title}</h5>
                  <p className="mb-0 text-muted">{service.description}</p>
                </ServiceBox>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <p className="lead fw-bold">Simplifiez votre gestion sociale</p>
            <p className="text-muted">
              Nous vous proposons une gestion complète et personnalisée de votre paie et de vos salariés. 
              Contactez-nous dès aujourd'hui pour une première consultation et découvrez comment nous pouvons vous accompagner.
            </p>
          </div>
        </div>
      </section>

      <section id="faq-section" className="container-fluid py-5">
        <div className="container">
          <h3 className="text-center fw-bold mb-5">Questions Fréquentes sur la Paie et RH</h3>
          <FAQContainer>
            
            
            <FAQContent>
              {faqData.map((faq, index) => (
                <FAQItem key={index} delay={`${index * 0.2}s`}>
                  <Question onClick={() => toggleFAQ(index)}>
                    {faq.question}
                    <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                  </Question>
                  {activeIndex === index && <Answer>{faq.answer}</Answer>}
                </FAQItem>
              ))}
            </FAQContent>
          </FAQContainer>
        </div>
      </section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.8), rgba(220, 53, 69, 0.8)), url("/assets/images/service-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }
        
        .advantage-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }
      `}</style>
    </>
  );
};

export default PayrollManagement;